/**
 * Local Authorization List section in SettingsTab (SPEC-001, 8f.4).
 *
 *   - Version pill (read-only, sourced from localAuthListVersions).
 *   - Add row form (idTag + status + optional expiryDate + parentIdTag).
 *   - Table of current entries with per-row Delete.
 *   - JSON import (paste) + JSON export (download).
 */
import { useEffect, useState } from 'react';
import { AuthStatus } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useLocalAuthListStore } from '@/state/useLocalAuthListStore';
import { useConfirm } from '@/hooks/useConfirm';
import { toast, toastError } from '@/state/useToastStore';

const STATUS_OPTIONS: readonly AuthStatus[] = Object.values(AuthStatus);

export function LocalAuthListSection(): JSX.Element {
  const stationId = useStationStore((s) => s.stationId);
  const entries = useLocalAuthListStore((s) => s.entries);
  const listVersion = useLocalAuthListStore((s) => s.listVersion);
  const refresh = useLocalAuthListStore((s) => s.refresh);
  const add = useLocalAuthListStore((s) => s.add);
  const remove = useLocalAuthListStore((s) => s.remove);
  const replaceAll = useLocalAuthListStore((s) => s.replaceAll);
  const confirm = useConfirm();

  const [idTag, setIdTag] = useState('');
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.Accepted);
  const [expiryDate, setExpiryDate] = useState('');
  const [parentIdTag, setParentIdTag] = useState('');
  const [importText, setImportText] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    void refresh(stationId);
  }, [refresh, stationId]);

  const handleAdd = (): void => {
    if (idTag.trim().length === 0) {
      toastError('idTag is required');
      return;
    }
    void add(stationId, {
      idTag: idTag.trim(),
      status,
      ...(expiryDate.trim() !== '' ? { expiryDate: expiryDate.trim() } : {}),
      ...(parentIdTag.trim() !== '' ? { parentIdTag: parentIdTag.trim() } : {}),
    }).then((ok) => {
      if (ok) {
        toast(`Added ${idTag.trim()}`);
        setIdTag('');
        setExpiryDate('');
        setParentIdTag('');
      } else {
        toastError('idTag must be 1–20 characters');
      }
    });
  };

  const handleExport = (): void => {
    const blob = new Blob([JSON.stringify({ listVersion, entries }, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `localAuthList-v${listVersion}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast('Local Auth List exported');
  };

  const handleImport = (): void => {
    try {
      const parsed = JSON.parse(importText) as unknown;
      // Accept either { entries: [...] } or a bare array of entries.
      let raw: unknown;
      if (Array.isArray(parsed)) raw = parsed;
      else if (parsed !== null && typeof parsed === 'object' && 'entries' in parsed)
        raw = parsed.entries;
      else throw new Error('Expected an array or an object with `entries`');
      if (!Array.isArray(raw)) throw new Error('entries must be an array');
      const out = raw.map((e) => {
        const o = e as Record<string, unknown>;
        if (typeof o['idTag'] !== 'string') throw new Error('entry.idTag missing');
        const s = o['status'];
        if (!STATUS_OPTIONS.includes(s as AuthStatus)) {
          throw new Error(`entry.status invalid: ${String(s)}`);
        }
        return {
          idTag: o['idTag'],
          status: s as AuthStatus,
          ...(typeof o['expiryDate'] === 'string' ? { expiryDate: o['expiryDate'] } : {}),
          ...(typeof o['parentIdTag'] === 'string' ? { parentIdTag: o['parentIdTag'] } : {}),
        };
      });
      void replaceAll(stationId, out).then(() => {
        toast(`Imported ${out.length} entries`);
        setImportText('');
        setImportOpen(false);
      });
    } catch (err) {
      toastError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section
      className="rounded border border-slate-700 bg-slate-900/60 p-3"
      data-testid="local-auth-list"
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-medium text-slate-100">Local Authorization List</h2>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span data-testid="local-auth-version">v{listVersion}</span>
          <span data-testid="local-auth-count">{entries.length} entries</span>
          <button
            type="button"
            onClick={handleExport}
            disabled={entries.length === 0}
            className="rounded bg-slate-700 px-2 py-0.5 text-[10px] text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="local-auth-export"
          >
            Export JSON
          </button>
          <button
            type="button"
            onClick={() => setImportOpen((v) => !v)}
            className="rounded bg-slate-700 px-2 py-0.5 text-[10px] text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
            data-testid="local-auth-import-toggle"
          >
            {importOpen ? 'Close' : 'Import JSON'}
          </button>
        </div>
      </header>

      {importOpen ? (
        <div className="mb-3 rounded border border-slate-700 bg-slate-950 p-2 text-xs">
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder='{"entries":[{"idTag":"DEMO","status":"Accepted"}]}'
            className="h-24 w-full rounded border border-slate-700 bg-slate-950 p-1 font-mono text-[10px] text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="local-auth-import-text"
          />
          <button
            type="button"
            onClick={handleImport}
            disabled={importText.trim() === ''}
            className="mt-2 rounded bg-primary-base px-2 py-0.5 text-[10px] text-primary-on hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="local-auth-import-apply"
          >
            Replace list with JSON
          </button>
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <input
          type="text"
          value={idTag}
          onChange={(e) => setIdTag(e.target.value)}
          placeholder="idTag (DEMO_TAG)"
          className="w-36 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
          data-testid="local-auth-input-idTag"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as AuthStatus)}
          className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
          data-testid="local-auth-input-status"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          placeholder="expiryDate (ISO, optional)"
          className="w-48 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
          data-testid="local-auth-input-expiry"
        />
        <input
          type="text"
          value={parentIdTag}
          onChange={(e) => setParentIdTag(e.target.value)}
          placeholder="parentIdTag (optional)"
          className="w-32 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
          data-testid="local-auth-input-parent"
        />
        <button
          type="button"
          onClick={handleAdd}
          className="rounded bg-primary-base px-2 py-1 text-primary-on hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary-base"
          data-testid="local-auth-add"
        >
          Add
        </button>
      </div>

      {entries.length === 0 ? (
        <p className="text-[11px] text-slate-500" data-testid="local-auth-empty">
          List is empty. Add an entry above, import JSON, or wait for the CSMS to push a
          SendLocalList.
        </p>
      ) : (
        <table className="w-full text-left text-xs" data-testid="local-auth-table">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1">idTag</th>
              <th className="px-2 py-1">status</th>
              <th className="px-2 py-1">expiry</th>
              <th className="px-2 py-1">parent</th>
              <th className="px-2 py-1 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr
                key={e.idTag}
                className="border-t border-slate-800"
                data-testid={`local-auth-row-${e.idTag}`}
              >
                <td className="px-2 py-1 font-mono text-slate-100">{e.idTag}</td>
                <td className="px-2 py-1 text-slate-300">{e.status}</td>
                <td className="px-2 py-1 font-mono text-[10px] text-slate-400">
                  {e.expiryDate ?? '—'}
                </td>
                <td className="px-2 py-1 font-mono text-[10px] text-slate-400">
                  {e.parentIdTag ?? '—'}
                </td>
                <td className="px-2 py-1 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      confirm(`delete local auth entry ${e.idTag}`, () => {
                        void remove(stationId, e.idTag).then(() => toast(`Deleted ${e.idTag}`));
                      });
                    }}
                    className="rounded bg-rose-700 px-2 py-0.5 text-[10px] text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
                    data-testid={`local-auth-delete-${e.idTag}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
